import type { AnnotationType, LetterGrade } from '@/lib/types';
import { LETTER_GRADE_THRESHOLDS } from '@/lib/constants';

/**
 * Convert a numeric score (0-100) to a letter grade.
 */
export function getLetterGrade(score: number): LetterGrade {
  if (score >= LETTER_GRADE_THRESHOLDS.A) return 'A';
  if (score >= LETTER_GRADE_THRESHOLDS.B) return 'B';
  if (score >= LETTER_GRADE_THRESHOLDS.C) return 'C';
  if (score >= LETTER_GRADE_THRESHOLDS.D) return 'D';
  return 'F';
}

/**
 * Get the Tailwind color class for a letter grade.
 */
export function getGradeColor(grade: LetterGrade): string {
  const colors: Record<LetterGrade, string> = {
    A: 'text-emerald-600',
    B: 'text-blue-600',
    C: 'text-amber-500',
    D: 'text-orange-500',
    F: 'text-red-600',
  };
  return colors[grade];
}

/**
 * Get the Tailwind background color class for a letter grade.
 */
export function getGradeBgColor(grade: LetterGrade): string {
  const colors: Record<LetterGrade, string> = {
    A: 'bg-emerald-100',
    B: 'bg-blue-100',
    C: 'bg-amber-100',
    D: 'bg-orange-100',
    F: 'bg-red-100',
  };
  return colors[grade];
}

/**
 * Get the color for an annotation highlight based on type.
 */
export function getAnnotationColor(type: AnnotationType): string {
  const colors: Record<AnnotationType, string> = {
    vague: 'bg-red-100 border-red-300',
    redundant: 'bg-amber-100 border-amber-300',
    'missing-context': 'bg-orange-100 border-orange-300',
    good: 'bg-emerald-100 border-emerald-300',
  };
  return colors[type];
}

/**
 * Get the text color for annotation type labels.
 */
export function getAnnotationTextColor(type: AnnotationType): string {
  const colors: Record<AnnotationType, string> = {
    vague: 'text-red-700',
    redundant: 'text-amber-700',
    'missing-context': 'text-orange-700',
    good: 'text-emerald-700',
  };
  return colors[type];
}

/**
 * Get Tailwind color for a numeric score.
 */
export function getScoreColor(score: number): string {
  return getGradeColor(getLetterGrade(score));
}

/**
 * Format a numeric score (0-100) to 1 decimal place for display.
 */
export function formatScore(score: number): string {
  return score.toFixed(1);
}

/**
 * Get Tailwind badge classes for a numeric score.
 */
export function getScoreBadgeClasses(score: number): string {
  const grade = getLetterGrade(score);
  return `${getGradeBgColor(grade)} ${getGradeColor(grade)}`;
}

/**
 * Format a score trend number for display.
 */
export function formatTrendLabel(trend: number | null): string {
  if (!trend || trend === 0) return 'No change';
  const sign = trend > 0 ? '+' : '';
  return `${sign}${trend.toFixed(1)} pts`;
}

/**
 * Get trend direction from a numeric value.
 */
export function getScoreTrend(trend: number | null): 'up' | 'down' | 'neutral' {
  if (!trend || trend === 0) return 'neutral';
  return trend > 0 ? 'up' : 'down';
}

/**
 * Format a date string for display.
 */
export function formatCoachingDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * SVG ring path calculation for ScoreRing.
 */
export function calculateRingPath(
  percentage: number,
  radius: number,
  strokeWidth: number
): { circumference: number; dashOffset: number; cx: number; cy: number; r: number } {
  const r = radius - strokeWidth / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - Math.max(0, Math.min(100, percentage)) / 100);
  return { circumference, dashOffset, cx: radius, cy: radius, r };
}
